import React, { useState } from 'react';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles, Music, SlidersHorizontal, Layers, Trophy, Share2 } from 'lucide-react';

const SLIDES = [
  {
    icon: Music,
    color: 'from-indigo-500 to-blue-500',
    title: 'Choose Your Preset',
    description: 'Pick from 27 science-backed presets — focus, sleep, meditation, and more. Each one sets up the perfect frequency and beat configuration for you, with peer-reviewed citations where applicable.',
  },
  {
    icon: SlidersHorizontal,
    color: 'from-emerald-500 to-teal-500',
    title: 'Fine-Tune Your Session',
    description: 'Adjust beat type, base frequency, volume, and session duration. Enable the ambient synth for layered soundscapes. Set a sleep timer to auto-fade.',
  },
  {
    icon: Layers,
    color: 'from-amber-500 to-orange-500',
    title: 'Design Your Timeline',
    description: 'Drag nodes on the interactive timeline to create frequency journeys. Switch to Volume mode to automate loudness changes throughout your session.',
  },
  {
    icon: Trophy,
    color: 'from-purple-500 to-pink-500',
    title: 'Track Your Progress',
    description: 'Earn achievements as you listen — session milestones, streaks, and listening goals. View your session history to see how your practice evolves.',
  },
  {
    icon: Share2,
    color: 'from-rose-500 to-red-500',
    title: 'Share & Collaborate',
    description: 'Save custom presets and share them with friends via unique links. They can load your exact configuration instantly.',
  },
];

const OnboardingTour = ({ onComplete, startPhase = 'slides' }) => {
  const [phase, setPhase] = useState(startPhase);
  const [slide, setSlide] = useState(0);

  const finish = () => {
    localStorage.setItem('neurowave_onboarding_done', 'true');
    onComplete();
  };

  const next = () => {
    if (slide < SLIDES.length - 1) setSlide(slide + 1);
    else finish();
  };

  const prev = () => {
    if (slide > 0) setSlide(slide - 1);
  };

  // ========== WELCOME ==========
  if (phase === 'welcome') {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="onboarding-welcome">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={finish} />
        <div className="relative z-[62] bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900/80 border border-white/10 rounded-2xl p-8 max-w-md w-full shadow-2xl text-center" data-testid="welcome-card">
          <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-indigo-500/20 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-indigo-400" />
          </div>
          <h2 className="text-white text-2xl font-bold mb-2">Welcome to NeuroWave</h2>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            Binaural beats, ambient synths, and brainwave entrainment — all in your browser.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => setPhase('slides')}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11 text-sm font-medium rounded-xl"
              data-testid="start-tour-btn"
            >
              Quick Tour (30 sec)
              <ChevronRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              variant="ghost"
              onClick={finish}
              className="w-full text-white/50 hover:text-white hover:bg-white/5 h-10 text-sm rounded-xl"
              data-testid="skip-tour-btn"
            >
              Skip, I'll explore on my own
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ========== SLIDESHOW ==========
  const current = SLIDES[slide];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="onboarding-tour">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={finish} />

      <div className="relative z-[62] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-white/10 rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden" data-testid="tour-slideshow">
        {/* Skip button */}
        <button
          onClick={finish}
          className="absolute top-4 right-4 text-white/30 hover:text-white/70 transition-colors z-10"
          data-testid="tour-skip-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="px-8 pt-10 pb-6 text-center">
          {/* Icon */}
          <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${current.color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-8 h-8 text-white" />
          </div>

          {/* Step indicator */}
          <span className="text-white/40 text-xs font-medium uppercase tracking-wider">
            {slide + 1} of {SLIDES.length}
          </span>

          {/* Title & description */}
          <h3 className="text-white text-xl font-bold mt-2 mb-3">{current.title}</h3>
          <p className="text-white/60 text-sm leading-relaxed max-w-sm mx-auto">{current.description}</p>
        </div>

        {/* Navigation bar */}
        <div className="px-8 pb-6 flex items-center justify-between">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={prev}
            disabled={slide === 0}
            className="text-white/50 hover:text-white text-sm h-9 px-3 disabled:opacity-0"
            data-testid="tour-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>

          {/* Progress dots */}
          <div className="flex gap-2">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === slide ? 'bg-indigo-400 w-5' : 'bg-white/20 hover:bg-white/40'
                }`}
                data-testid={`tour-dot-${i}`}
              />
            ))}
          </div>

          {/* Next / Finish button */}
          <Button
            onClick={next}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm h-9 px-4 rounded-lg"
            data-testid="tour-next-btn"
          >
            {slide === SLIDES.length - 1 ? "Let's Go" : 'Next'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
